import * as THREE from 'three';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import type { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

import type { Renderer, QualityOptions } from '../renderer';
import type { GameState } from '../../../core/state/gameState';
import type { GameEmitter } from '../../../core/events';
import type { EnemyState, ProjectileState, EntityId, EnemyKind } from '../../../core/types';
import type { Theme } from '../../../core/config/theme';
import { DEFAULT_THEME, themeForLevel } from '../../../core/config/theme';
import { ARENA, VISUALS, JUICE, labelOpacity } from '../../../core/config/gameConfig';
import { enemyColor, enemyShape } from '../../../core/config/enemyCatalog';
import { createDigitGeometry } from './textFactory';
import { createShapeGeometry, createStarGeometry } from './shapeFactory';
import { createEnemyMaterial } from './materials';
import { createGround, disposeGround, scrollGround } from './terrain';
import { Effects } from './effects';
import { ShapeShatter } from './shapeShatter';
import { DebrisPhysics } from './debrisPhysics';
import type { AmbienceEffect } from './ambience';
import { createAmbience } from './ambience';

// Per-enemy visual. In the 'digits' style (VISUALS.enemyStyle) the body IS a
// big extruded glass/metal digit — the classic CountStrike look. In 'shapes'
// style the body is the digit-encoding solid plus a billboarded label that
// fades out as levels rise (see LABELS in gameConfig).
interface EnemyVisual {
  group: THREE.Group;
  shape: THREE.Mesh;
  label: THREE.Mesh | null;
  /** Seconds remaining on the wrong-answer flash. */
  flash: number;
  baseEmissive: THREE.Color;
  baseEmissiveIntensity: number;
  /** Kept so materials can be rebuilt when the theme changes mid-run. */
  color: number;
  kind: EnemyKind;
}

const SPAWN_IN_SEC = 0.35;
const FLASH_SEC = 0.3;
const SHAKE_SEC = 0.6;

function disposeObject(obj: THREE.Object3D): void {
  obj.traverse((node) => {
    const mesh = node as THREE.Mesh;
    // Shared template geometries (shapeFactory/textFactory caches) outlive any
    // one mesh — never dispose those.
    if (mesh.geometry && !mesh.geometry.userData.shared) mesh.geometry.dispose();
    const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(m)) m.forEach((x) => x.dispose());
    else m?.dispose();
  });
}

/** Ease-out-back: overshoots slightly, gives spawns a "pop". */
function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

export class ThreeRenderer implements Renderer {
  private scene = new THREE.Scene();
  private camera!: THREE.PerspectiveCamera;
  private webgl!: THREE.WebGLRenderer;
  private font: Font | null = null;
  private envMap: THREE.Texture | null = null;
  private environmentEnabled = true;

  private theme: Theme;
  /** Guards against an older async theme load finishing after a newer one. */
  private themeLoadSeq = 0;

  private enemies = new Map<EntityId, EnemyVisual>();
  private projectileMeshes = new Map<EntityId, THREE.Group>();
  private playerMesh: THREE.Mesh | null = null;
  private lastWeapon = -1;

  // Projectile visuals are pooled: geometry/material shared across all shots
  // (creating lights or geometries per shot causes shader-compile hitches).
  private projectilePool: THREE.Group[] = [];
  private projCoreGeo: THREE.SphereGeometry | null = null;
  private projGlowGeo: THREE.SphereGeometry | null = null;
  private projTracerGeo: THREE.CylinderGeometry | null = null;
  private projCoreMat: THREE.MeshBasicMaterial | null = null;
  private projGlowMat: THREE.MeshBasicMaterial | null = null;
  private projTracerMat: THREE.MeshBasicMaterial | null = null;

  // Reused per frame to diff live entities against visuals without allocating.
  private readonly seenIds = new Set<EntityId>();

  private ambient!: THREE.AmbientLight;
  private sun!: THREE.DirectionalLight;
  private spotA!: THREE.SpotLight;
  private spotB!: THREE.SpotLight;
  private ground: THREE.Mesh | null = null;

  private effects!: Effects;
  private shatter!: ShapeShatter;
  private cameraBase = new THREE.Vector3();
  private shake = 0;
  private shakeTotal = SHAKE_SEC;
  private shakeAmp = 0.25;
  private unsubscribes: Array<() => void> = [];

  // Post-processing (bloom) — built on demand behind the quality toggle.
  private composer: EffectComposer | null = null;
  private bloomEnabled = false;

  // World-motion toggles + ambience.
  private motionEnabled = true;
  private ambience: AmbienceEffect[] = [];

  // Rigid-body debris (lazy WASM).
  private debrisEnabled = false;
  private debrisLoading = false;

  // Power-up stars: shared geometry + one shared golden material.
  private powerupMeshes = new Map<EntityId, THREE.Mesh>();
  private starMat: THREE.MeshStandardMaterial | null = null;

  private lastHintId: EntityId | null = null;
  private readonly tmpVec = new THREE.Vector3();

  constructor(theme: Theme = DEFAULT_THEME) {
    this.theme = theme;
  }

  get canvas(): HTMLCanvasElement | null {
    return this.webgl?.domElement ?? null;
  }

  async init(container: HTMLElement, events: GameEmitter): Promise<void> {
    this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.camera.position.set(0, 9.1, 8);
    this.camera.rotation.set(THREE.MathUtils.degToRad(-30), 0, 0);
    this.cameraBase.copy(this.camera.position);

    // alpha:false — the scene always paints an opaque sky, and an opaque
    // canvas skips page compositing cost.
    this.webgl = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.webgl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.webgl.setSize(window.innerWidth, window.innerHeight);
    this.webgl.shadowMap.enabled = true;
    this.webgl.shadowMap.type = THREE.PCFSoftShadowMap;
    this.webgl.outputColorSpace = THREE.SRGBColorSpace;
    this.webgl.toneMapping = THREE.ACESFilmicToneMapping;
    container.appendChild(this.webgl.domElement);

    this.setupLights();
    this.effects = new Effects(this.scene);
    this.shatter = new ShapeShatter(this.scene);

    this.subscribe(events);
    this.applyTheme(this.theme);

    // Font is required before any text mesh; sky/ground stream in behind it.
    this.font = await new FontLoader().loadAsync('/fonts/helvetiker_regular.typeface.json');
  }

  private subscribe(events: GameEmitter): void {
    this.unsubscribes.push(
      events.on('enemyDestroyed', ({ enemy, projectile }) => {
        this.shatterEnemy(enemy, projectile);
        this.addShake(JUICE.killShakeSec, JUICE.killShakeAmp);
      }),
      events.on('answerWrong', ({ enemy }) => this.flashEnemy(enemy.id)),
      events.on('levelChanged', ({ level }) => this.onLevelChanged(level)),
      // New curriculum stage: the core cleared the board; burst every live
      // enemy at once as the celebration beat.
      events.on('stageChanged', () => this.shatterAll()),
      // Muzzle flash: a brief spark burst at the barrel.
      events.on('projectileFired', () => {
        this.effects.explode(ARENA.playerPosition, this.theme.projectileColor, 10, 0.18, 5);
      }),
      events.on('powerUpCollected', ({ position }) => {
        this.effects.explode(position, 0xffd166, 42, 0.6, 3.2);
      }),
      events.on('gameOver', () => {
        this.effects.explode(ARENA.playerPosition);
        this.addShake(SHAKE_SEC, 0.25);
      }),
      events.on('reset', () => {
        this.clearWorld();
        this.onLevelChanged(1); // quit to home = fresh level-1 atmosphere
      }),
      events.on('started', () => {
        this.clearWorld();
        this.onLevelChanged(1);
      }),
    );
  }

  private onLevelChanged(level: number): void {
    const next = themeForLevel(level);
    if (next.id !== this.theme.id) this.applyTheme(next);
  }

  // ---- Theme application (the aesthetics seam made live) ----

  private applyTheme(theme: Theme): void {
    this.theme = theme;
    const seq = ++this.themeLoadSeq;

    this.scene.background = new THREE.Color(theme.backgroundColor);
    this.scene.fog = theme.fog ? new THREE.FogExp2(theme.fog.color, theme.fog.density) : null;
    this.webgl.toneMappingExposure = theme.exposure;

    const { lights } = theme;
    this.ambient.intensity = lights.ambient;
    this.ambient.color.setHex(lights.ambientColor);
    this.sun.intensity = lights.sun;
    this.sun.color.setHex(lights.sunColor);
    this.sun.position.set(lights.sunPosition[0], lights.sunPosition[1], lights.sunPosition[2]);
    this.spotA.intensity = lights.fill;
    this.spotB.intensity = lights.fill;

    this.projCoreMat?.color.setHex(theme.projectileColor);
    this.projGlowMat?.color.setHex(theme.projectileColor);

    if (this.ground) disposeGround(this.ground);
    if (this.ground) this.scene.remove(this.ground);
    this.ground = createGround(theme.ground);
    this.scene.add(this.ground);

    // Per-theme sky/periphery life: clouds by day, aurora and shooting stars
    // by night. Motion stays out of the play corridor.
    for (const fx of this.ambience) {
      this.scene.remove(fx.object);
      fx.dispose();
    }
    this.ambience = createAmbience(theme.ambience);
    for (const fx of this.ambience) this.scene.add(fx.object);

    // Retune enemies already alive: they were built with the previous theme's
    // material presets (e.g. day's low emissive) and would otherwise go dim
    // against a dark sky until they die.
    const env = this.environmentEnabled ? this.envMap : null;
    for (const visual of this.enemies.values()) {
      const fresh = createEnemyMaterial(visual.kind, visual.color, theme, env) as THREE.MeshStandardMaterial;
      (visual.shape.material as THREE.Material).dispose();
      visual.shape.material = fresh;
      visual.baseEmissive = fresh.emissive?.clone() ?? new THREE.Color(0x000000);
      visual.baseEmissiveIntensity = fresh.emissiveIntensity ?? 0;
      visual.flash = 0;
    }

    void this.loadSky(theme, seq);
  }

  private async loadSky(theme: Theme, seq: number): Promise<void> {
    if (!theme.environmentUrl) {
      this.setEnvTexture(null);
      return;
    }
    try {
      let texture: THREE.Texture;
      if (theme.environmentUrl.endsWith('.hdr')) {
        texture = await new RGBELoader().loadAsync(theme.environmentUrl);
      } else {
        texture = await new THREE.TextureLoader().loadAsync(theme.environmentUrl);
        texture.colorSpace = THREE.SRGBColorSpace;
      }
      texture.mapping = THREE.EquirectangularReflectionMapping;
      // A newer theme superseded this load — drop it.
      if (seq !== this.themeLoadSeq) {
        texture.dispose();
        return;
      }
      this.setEnvTexture(texture);
    } catch {
      // Sky failed to load (offline / 404): synthesize a gradient sky from the
      // theme palette so reflections and backdrop still match the theme
      // instead of keeping the previous theme's environment.
      if (seq === this.themeLoadSeq) this.setEnvTexture(this.proceduralSky(theme));
    }
  }

  /** Canvas-gradient equirect fallback matching the theme's palette. */
  private proceduralSky(theme: Theme): THREE.Texture {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    const top = new THREE.Color(theme.backgroundColor).multiplyScalar(1.25);
    const bottom = new THREE.Color(theme.backgroundColor).multiplyScalar(0.35);
    const grd = ctx.createLinearGradient(0, 0, 0, canvas.height);
    grd.addColorStop(0, `#${top.getHexString()}`);
    grd.addColorStop(0.55, `#${new THREE.Color(theme.backgroundColor).getHexString()}`);
    grd.addColorStop(1, `#${bottom.getHexString()}`);
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.mapping = THREE.EquirectangularReflectionMapping;
    return texture;
  }

  private setEnvTexture(texture: THREE.Texture | null): void {
    if (this.envMap) this.envMap.dispose();
    this.envMap = texture;
    // The sky is always the visual backdrop; `environment` (reflections) obeys
    // the user's reflections toggle separately. LDR (jpg) skies get crushed by
    // ACES tone mapping, so each theme sets its own backdrop boost.
    if (texture) {
      this.scene.background = texture;
      this.scene.backgroundIntensity = this.theme.backgroundIntensity;
    }
    this.scene.environment = this.environmentEnabled ? texture : null;
    this.refreshGlassEnvMaps();
  }

  private refreshGlassEnvMaps(): void {
    const env = this.environmentEnabled ? this.envMap : null;
    for (const visual of this.enemies.values()) {
      const mat = visual.shape.material as THREE.MeshPhysicalMaterial;
      if ('envMap' in mat) {
        mat.envMap = env;
        mat.needsUpdate = true;
      }
    }
  }

  // ---- Death / feedback FX ----

  private shatterEnemy(enemy: EnemyState, projectile?: ProjectileState): void {
    const visual = this.enemies.get(enemy.id);
    if (!visual) return;
    this.enemies.delete(enemy.id);
    // Debris sprays away from the killing shot; shards carry the enemy's own
    // color/kind. Shatter clones the geometry (it may be a shared template)
    // and disposes nothing — the per-instance material is ours to free.
    this.shatter.shatter(visual.shape, {
      impactDir: projectile?.direction,
      kind: visual.kind,
      color: visual.color,
    });
    (visual.shape.material as THREE.Material).dispose();
    if (visual.label) {
      visual.group.remove(visual.label);
      disposeObject(visual.label);
    }
    this.scene.remove(visual.group);
  }

  private flashEnemy(id: EntityId): void {
    const visual = this.enemies.get(id);
    if (visual) visual.flash = FLASH_SEC;
  }

  /** Stage-clear spectacle: every live enemy bursts upward simultaneously. */
  private shatterAll(): void {
    for (const visual of this.enemies.values()) {
      this.shatter.shatter(visual.shape, {
        impactDir: { x: 0, y: 0.9, z: -0.4 },
        kind: visual.kind,
        color: visual.color,
      });
      (visual.shape.material as THREE.Material).dispose();
      if (visual.label) {
        visual.group.remove(visual.label);
        disposeObject(visual.label);
      }
      this.scene.remove(visual.group);
    }
    this.enemies.clear();
  }

  // ---- Frame sync ----

  sync(state: GameState, dt: number): void {
    if (this.font) {
      this.syncPlayer(state.weapon);
      this.syncEnemies(state, dt);
      this.syncProjectiles(state.projectiles, dt);
      this.syncPowerUps(state, dt);
    }
    this.effects.update(dt);
    this.shatter.update(dt);
    this.updateShake(dt);
    this.updateWorldMotion(state, dt);
  }

  /** The living world: drifting ground, slowly turning sky, ambience FX. All
   *  of it below/behind the enemies; all of it zeroed by reduce-motion. */
  private updateWorldMotion(state: GameState, dt: number): void {
    for (const fx of this.ambience) fx.update(dt, this.motionEnabled);
    if (!this.motionEnabled) return;
    if (this.ground) {
      // The march speeds up gently as levels ramp — the difficulty is felt
      // underfoot, not just seen in the spawn rate.
      const levelScale = Math.min(1 + (state.level - 1) * 0.06, 2);
      scrollGround(this.ground, dt, this.theme.ground.scrollSpeed * levelScale);
    }
    const yaw = this.theme.ambience.skyYawSpeed * dt;
    this.scene.backgroundRotation.y += yaw;
    this.scene.environmentRotation.y += yaw;
  }

  /** Bigger shakes win; small kill-shakes never cut a game-over rumble short. */
  private addShake(sec: number, amp: number): void {
    if (amp >= this.shakeAmp || this.shake <= 0) {
      this.shake = sec;
      this.shakeTotal = sec;
      this.shakeAmp = amp;
    }
  }

  private updateShake(dt: number): void {
    if (this.shake <= 0) return;
    this.shake -= dt;
    const k = Math.max(this.shake / this.shakeTotal, 0) * this.shakeAmp;
    this.camera.position.set(
      this.cameraBase.x + (Math.random() - 0.5) * k,
      this.cameraBase.y + (Math.random() - 0.5) * k,
      this.cameraBase.z + (Math.random() - 0.5) * k,
    );
    if (this.shake <= 0) this.camera.position.copy(this.cameraBase);
  }

  private syncPlayer(weapon: number): void {
    if (weapon === this.lastWeapon && this.playerMesh) return;
    this.lastWeapon = weapon;
    if (!this.playerMesh) {
      const mat = new THREE.MeshStandardMaterial({
        color: this.theme.playerColor,
        metalness: 0.3,
        roughness: 0.35,
        emissive: 0x222222,
      });
      const mesh = new THREE.Mesh(createDigitGeometry(this.font!, String(weapon)), mat);
      mesh.castShadow = true;
      mesh.position.set(ARENA.playerPosition.x, ARENA.playerPosition.y, ARENA.playerPosition.z);
      mesh.rotation.x = THREE.MathUtils.degToRad(-15);
      this.scene.add(mesh);
      this.playerMesh = mesh;
      return;
    }
    // Weapon scrolls fast — swap the shared memoized glyph, never rebuild.
    this.playerMesh.geometry = createDigitGeometry(this.font!, String(weapon));
    (this.playerMesh.material as THREE.MeshStandardMaterial).color.setHex(this.theme.playerColor);
  }

  private syncEnemies(state: GameState, dt: number): void {
    const seen = this.seenIds;
    seen.clear();
    // Band-relative: labels come back as training wheels whenever a new
    // curriculum stage starts (each stage is a new mapping to learn).
    const labelAlpha = labelOpacity(state.level - state.stageFromLevel + 1);

    // Hint power-up: when the highlighted enemy changes, restore the old one.
    if (state.hintTargetId !== this.lastHintId) {
      const old = this.lastHintId != null ? this.enemies.get(this.lastHintId) : undefined;
      if (old && old.flash <= 0) {
        const mat = old.shape.material as THREE.MeshStandardMaterial;
        mat.emissive.copy(old.baseEmissive);
        mat.emissiveIntensity = old.baseEmissiveIntensity;
      }
      this.lastHintId = state.hintTargetId;
    }

    for (const e of state.enemies) {
      seen.add(e.id);
      let visual = this.enemies.get(e.id);
      if (!visual) {
        visual = this.createEnemyVisual(e, labelAlpha);
        this.enemies.set(e.id, visual);
        this.scene.add(visual.group);
      }
      const { group, shape, label } = visual;
      group.position.set(e.position.x, e.position.y, e.position.z);
      shape.rotation.y += e.spin * dt;

      // Spawn pop: scale in with a slight overshoot.
      const age = state.elapsed - e.spawnedAt;
      const t = Math.min(age / SPAWN_IN_SEC, 1);
      group.scale.setScalar(ARENA.enemyScale * (t < 1 ? easeOutBack(t) : 1));

      // Digit label: billboard to camera, opacity from the level fade policy.
      if (label) {
        label.quaternion.copy(this.camera.quaternion);
        const mat = label.material as THREE.MeshBasicMaterial;
        mat.opacity = labelAlpha;
        label.visible = labelAlpha > 0.01;
      }

      // Wrong-answer flash: pulse the emissive red, then restore.
      if (visual.flash > 0) {
        visual.flash -= dt;
        const mat = shape.material as THREE.MeshStandardMaterial;
        const f = Math.max(visual.flash / FLASH_SEC, 0);
        if (f > 0) {
          mat.emissive.setHex(0xff2222);
          mat.emissiveIntensity = 1.4 * f;
        } else {
          mat.emissive.copy(visual.baseEmissive);
          mat.emissiveIntensity = visual.baseEmissiveIntensity;
        }
      } else if (e.id === state.hintTargetId) {
        // Hint glow: pulse the enemy the current answer would destroy.
        const mat = shape.material as THREE.MeshStandardMaterial;
        mat.emissiveIntensity =
          visual.baseEmissiveIntensity + 0.9 * (0.5 + 0.5 * Math.sin(state.elapsed * 6));
      }
    }

    for (const [id, visual] of this.enemies) {
      if (!seen.has(id)) {
        this.scene.remove(visual.group);
        disposeObject(visual.group);
        this.enemies.delete(id);
      }
    }
  }

  private createEnemyVisual(e: EnemyState, labelAlpha: number): EnemyVisual {
    const color = enemyColor(e.value);
    const kind: EnemyKind = e.kind;
    const material = createEnemyMaterial(
      kind,
      color,
      this.theme,
      this.environmentEnabled ? this.envMap : null,
    ) as THREE.MeshStandardMaterial;

    // 'digits': the enemy body IS the extruded digit (legacy look). 'shapes':
    // the digit-encoding solid, with a label handled below. Geometries are
    // shared templates — meshes scale, never the geometry.
    const digitStyle = VISUALS.enemyStyle === 'digits';
    const shape = new THREE.Mesh(
      digitStyle ? createDigitGeometry(this.font!, e.display) : createShapeGeometry(enemyShape(e.value)),
      material,
    );
    shape.castShadow = true;
    // Digit glyphs sit on their baseline; drop them so they spin around the
    // enemy's nominal center height instead of floating above it.
    if (digitStyle) shape.position.y = -0.35;

    const group = new THREE.Group();
    group.add(shape);

    // Digit label floats above the solid ('shapes' style only — a digit body
    // needs no caption). Starts invisible-scale until spawn pop.
    let label: THREE.Mesh | null = null;
    if (!digitStyle && this.font && labelAlpha > 0) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 });
      label = new THREE.Mesh(createDigitGeometry(this.font, e.display), mat);
      label.scale.setScalar(0.42);
      label.position.set(0, 0.95, 0);
      group.add(label);
    }
    group.scale.setScalar(0.001);

    return {
      group,
      shape,
      label,
      flash: 0,
      baseEmissive: material.emissive?.clone() ?? new THREE.Color(0x000000),
      baseEmissiveIntensity: material.emissiveIntensity ?? 0,
      color,
      kind,
    };
  }

  private syncProjectiles(projectiles: ProjectileState[], dt: number): void {
    const seen = this.seenIds;
    seen.clear();
    for (const p of projectiles) {
      seen.add(p.id);
      let mesh = this.projectileMeshes.get(p.id);
      if (!mesh) {
        mesh = this.acquireProjectileVisual();
        this.projectileMeshes.set(p.id, mesh);
        this.scene.add(mesh);
      }
      mesh.position.set(p.position.x, p.position.y, p.position.z);
      // Face the direction of travel so the tracer trails correctly.
      this.tmpVec.set(
        p.position.x + p.direction.x,
        p.position.y + p.direction.y,
        p.position.z + p.direction.z,
      );
      mesh.lookAt(this.tmpVec);
      mesh.userData.pulse = (mesh.userData.pulse as number) + dt * 5;
      // Glow-scale pulse stands in for the old PointLight throb (per-shot
      // lights forced shader recompiles every time the light count changed).
      const pulse = 1 + Math.sin(mesh.userData.pulse as number) * 0.2;
      mesh.children[1]?.scale.setScalar(pulse);
    }
    for (const [id, mesh] of this.projectileMeshes) {
      if (!seen.has(id)) {
        this.releaseProjectileVisual(mesh);
        this.projectileMeshes.delete(id);
      }
    }
  }

  private acquireProjectileVisual(): THREE.Group {
    const pooled = this.projectilePool.pop();
    if (pooled) {
      pooled.userData.pulse = 0;
      pooled.visible = true;
      return pooled;
    }
    if (!this.projCoreGeo) {
      this.projCoreGeo = new THREE.SphereGeometry(0.15, 16, 16);
      this.projGlowGeo = new THREE.SphereGeometry(0.3, 16, 16);
      // Tracer: a thin Z-aligned rod trailing the shot.
      this.projTracerGeo = new THREE.CylinderGeometry(0.045, 0.012, 1.8, 6);
      this.projTracerGeo.rotateX(Math.PI / 2);
      this.projCoreMat = new THREE.MeshBasicMaterial({ color: this.theme.projectileColor });
      this.projGlowMat = new THREE.MeshBasicMaterial({
        color: this.theme.projectileColor,
        transparent: true,
        opacity: 0.3,
        side: THREE.BackSide,
      });
      this.projTracerMat = new THREE.MeshBasicMaterial({
        color: this.theme.projectileColor,
        transparent: true,
        opacity: 0.4,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
    }
    const group = new THREE.Group();
    const tracer = new THREE.Mesh(this.projTracerGeo!, this.projTracerMat!);
    tracer.position.z = -1.0;
    group.add(
      new THREE.Mesh(this.projCoreGeo, this.projCoreMat!),
      new THREE.Mesh(this.projGlowGeo!, this.projGlowMat!),
      tracer,
    );
    group.userData = { pulse: 0 };
    return group;
  }

  private releaseProjectileVisual(group: THREE.Group): void {
    this.scene.remove(group);
    this.projectilePool.push(group);
  }

  /** Golden drifting stars — shoot one (or let it reach you) to collect. */
  private syncPowerUps(state: GameState, dt: number): void {
    const seen = this.seenIds;
    seen.clear();
    for (const p of state.powerups) {
      seen.add(p.id);
      let mesh = this.powerupMeshes.get(p.id);
      if (!mesh) {
        if (!this.starMat) {
          this.starMat = new THREE.MeshStandardMaterial({
            color: 0xffc84d,
            emissive: 0xffaa00,
            emissiveIntensity: 0.85,
            metalness: 0.3,
            roughness: 0.3,
          });
        }
        mesh = new THREE.Mesh(createStarGeometry(), this.starMat);
        mesh.scale.setScalar(1.4);
        this.powerupMeshes.set(p.id, mesh);
        this.scene.add(mesh);
      }
      const bob = Math.sin(state.elapsed * 3 + p.id) * 0.18;
      mesh.position.set(p.position.x, p.position.y + bob, p.position.z);
      mesh.rotation.y += 2.4 * dt;
      mesh.rotation.x += 0.7 * dt;
    }
    for (const [id, mesh] of this.powerupMeshes) {
      if (!seen.has(id)) {
        // Geometry and material are shared — just detach.
        this.scene.remove(mesh);
        this.powerupMeshes.delete(id);
      }
    }
  }

  private clearWorld(): void {
    for (const visual of this.enemies.values()) {
      this.scene.remove(visual.group);
      disposeObject(visual.group);
    }
    for (const mesh of this.projectileMeshes.values()) {
      this.releaseProjectileVisual(mesh);
    }
    for (const mesh of this.powerupMeshes.values()) {
      this.scene.remove(mesh);
    }
    this.enemies.clear();
    this.projectileMeshes.clear();
    this.powerupMeshes.clear();
    this.lastHintId = null;
    this.effects.clear();
    this.shatter.clear();
    this.shake = 0;
    this.camera.position.copy(this.cameraBase);
  }

  render(): void {
    if (this.bloomEnabled && this.composer) this.composer.render();
    else this.webgl.render(this.scene, this.camera);
  }

  setEnvironmentEnabled(enabled: boolean): void {
    this.environmentEnabled = enabled;
    this.scene.environment = enabled ? this.envMap : null;
    this.refreshGlassEnvMaps();
  }

  setQuality(opts: QualityOptions): void {
    if (opts.motion !== undefined) this.motionEnabled = opts.motion;

    if (opts.bloom !== undefined) {
      this.bloomEnabled = opts.bloom;
      if (opts.bloom && !this.composer && this.webgl) {
        this.composer = new EffectComposer(this.webgl);
        this.composer.addPass(new RenderPass(this.scene, this.camera));
        // Subtle: the glass digits' self-colored emissive is what blooms.
        this.composer.addPass(
          new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.38, 0.5, 0.82),
        );
      }
    }

    if (opts.debrisPhysics !== undefined && opts.debrisPhysics !== this.debrisEnabled) {
      this.debrisEnabled = opts.debrisPhysics;
      if (opts.debrisPhysics) {
        if (!this.debrisLoading) {
          this.debrisLoading = true;
          void DebrisPhysics.create(ARENA.groundY).then((physics) => {
            this.debrisLoading = false;
            // The toggle may have flipped back while the WASM loaded.
            if (physics && this.debrisEnabled) this.shatter.setPhysics(physics);
            else physics?.dispose();
          });
        }
      } else {
        this.shatter.setPhysics(null);
      }
    }
  }

  projectToScreen(pos: { x: number; y: number; z: number }): { x: number; y: number } | null {
    this.tmpVec.set(pos.x, pos.y, pos.z).project(this.camera);
    if (this.tmpVec.z > 1) return null; // behind the camera
    return {
      x: (this.tmpVec.x * 0.5 + 0.5) * window.innerWidth,
      y: (-this.tmpVec.y * 0.5 + 0.5) * window.innerHeight,
    };
  }

  resize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    // DPR can change mid-session (window dragged between displays).
    this.webgl.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.webgl.setSize(window.innerWidth, window.innerHeight);
    this.composer?.setSize(window.innerWidth, window.innerHeight);
  }

  dispose(): void {
    for (const off of this.unsubscribes) off();
    this.unsubscribes = [];
    this.clearWorld();
    this.shatter.setPhysics(null);
    for (const fx of this.ambience) {
      this.scene.remove(fx.object);
      fx.dispose();
    }
    this.ambience = [];
    if (this.playerMesh) disposeObject(this.playerMesh);
    this.projCoreGeo?.dispose();
    this.projGlowGeo?.dispose();
    this.projTracerGeo?.dispose();
    this.projCoreMat?.dispose();
    this.projGlowMat?.dispose();
    this.projTracerMat?.dispose();
    this.starMat?.dispose();
    this.composer?.dispose();
    this.projectilePool.length = 0;
    if (this.ground) {
      this.scene.remove(this.ground);
      disposeGround(this.ground);
    }
    if (this.envMap) this.envMap.dispose();
    this.webgl.dispose();
    this.webgl.domElement.remove();
  }

  private setupLights(): void {
    this.ambient = new THREE.AmbientLight(0xffffff, 1);
    this.scene.add(this.ambient);

    this.sun = new THREE.DirectionalLight(0xffffff, 1.5);
    this.sun.castShadow = true;
    // Frustum sized to the actual play corridor (x ±10, z −20..0) instead of
    // ±50: same perceived shadow density from a quarter of the texel budget.
    this.sun.shadow.mapSize.set(1024, 1024);
    this.sun.shadow.camera.near = 0.5;
    this.sun.shadow.camera.far = 120;
    Object.assign(this.sun.shadow.camera, { left: -24, right: 24, top: 24, bottom: -24 });
    this.sun.shadow.radius = 4;
    this.scene.add(this.sun);

    this.spotA = new THREE.SpotLight(0xffffcc, 1.5, 100, Math.PI / 6, 0.2);
    this.spotA.position.set(-20, 15, 10);
    this.spotB = new THREE.SpotLight(0xccffff, 1.5, 100, Math.PI / 6, 0.2);
    this.spotB.position.set(20, 15, 10);
    this.scene.add(this.spotA, this.spotB);
  }
}
